import { createInitialLifeMap } from './initial'
import { layoutLifeMap } from './layout'
import getThingData from './queries/getThingData'

export const lifeMapDefaultConfig = {
  lifeMap: {
    slotCount: 6,
    ringCount: 1,
    radius: 240,
    autoSaveOnAdd: true,
    defaultRingBlock: 'lifeMapNode',
    autoConnectSlots: false,
    canEditContent: true,
    showProgressMeter: true,
    hideProgressWhenComplete: true,
    hexagonOrientation: 'pointy',
    defaultSlotData: [{
      levelTypeId: 279,
      data: {
        categoryCode: 'lifePlan'
      }
    }],
    contentStyles: {},
    headerStyles: {}
  },
  canvasSettings: {
    pathType: 'bezier',
    hideHandlers: false,
    nodesEditable: true
  },
  nodeLinking: {
    levels: ['thing'],
    enabled: true
  }
}

export const lifeMapPlugin = {
  key: 'lifeMap',
  defaultConfig: lifeMapDefaultConfig,
  createInitial: createInitialLifeMap,
  layout: layoutLifeMap,
  buildInitialLayout: ({ config, clientId }) => {
    const initial = createInitialLifeMap(config, clientId)
    const laidOut = layoutLifeMap({
      nodes: initial.nodes.map(n => ({ ...n })),
      edges: (initial.edges || []).map(e => ({ ...e })),
      centerId: initial.centerId,
      config
    })
    return laidOut ?? { nodes: initial.nodes, edges: initial.edges || [] }
  },
  getLinkedEntitiesFromNodes: (nodes) => (nodes || []).map((node) => node?.data?.entityProps).filter(Boolean),
  fetchSlotData: async ({ slotData, levelTypeId, levelId }) => {
    if (!slotData?.levelTypeId) return null

    const queryMap = {
      279: getThingData
    }

    const query = queryMap[slotData?.levelTypeId]
    if (!query) {
      console.warn(`No query mapping for levelTypeId: ${slotData?.levelTypeId}`)
      return null
    }

    return {
      levelTypeId: slotData?.levelTypeId,
      data: await query(slotData, levelTypeId, levelId)
    }
  },
  interactions: {
    nodeCreationEnabled: false,
    nodeLinkingEnabled: false
  },
  onConnect: (connection, nodes, config) => {
    const sourceNode = nodes.find(n => n.id === connection.source)
    const targetNode = nodes.find(n => n.id === connection.target)

    if (sourceNode && targetNode) {
      const sourceIsHexagon = sourceNode.type === 'hexagon'
      const targetIsHexagon = targetNode.type === 'hexagon'

      // Get orientation from node data or config
      const orientation = sourceNode?.data?.nodeProps?.orientation ||
                         targetNode?.data?.nodeProps?.orientation ||
                         config?.lifeMap?.hexagonOrientation ||
                         'pointy'

      if ((sourceIsHexagon || targetIsHexagon) && (!connection.sourceHandle || !connection.targetHandle)) {
        // Calculate angle from source to target
        const dx = targetNode.position.x - sourceNode.position.x
        const dy = targetNode.position.y - sourceNode.position.y
        const angle = Math.atan2(dy, dx)
        const deg = ((angle * 180 / Math.PI) + 360) % 360

        if (!connection.sourceHandle) {
          if (orientation === 'flat') {
            // Flat-top hexagon handle selection
            if (deg >= 330 || deg < 30) connection.sourceHandle = 'handle_right'
            else if (deg >= 30 && deg < 60) connection.sourceHandle = 'handle_rightBottom'
            else if (deg >= 60 && deg < 120) connection.sourceHandle = 'handle_bottom'
            else if (deg >= 120 && deg < 150) connection.sourceHandle = 'handle_leftBottom'
            else if (deg >= 150 && deg < 210) connection.sourceHandle = 'handle_left'
            else if (deg >= 210 && deg < 240) connection.sourceHandle = 'handle_leftTop'
            else if (deg >= 240 && deg < 300) connection.sourceHandle = 'handle_top'
            else connection.sourceHandle = 'handle_rightTop'
          } else {
            // Pointy-top hexagon (original)
            if (deg >= 337.5 || deg < 22.5) connection.sourceHandle = 'handle_right'
            else if (deg >= 22.5 && deg < 67.5) connection.sourceHandle = 'handle_bottom'
            else if (deg >= 67.5 && deg < 112.5) connection.sourceHandle = 'handle_bottom'
            else if (deg >= 112.5 && deg < 157.5) connection.sourceHandle = 'handle_bottom'
            else if (deg >= 157.5 && deg < 202.5) connection.sourceHandle = 'handle_left'
            else if (deg >= 202.5 && deg < 247.5) connection.sourceHandle = 'handle_top'
            else if (deg >= 247.5 && deg < 292.5) connection.sourceHandle = 'handle_top'
            else connection.sourceHandle = 'handle_top'
          }
        }

        if (!connection.targetHandle) {
          const oppositeAngle = (deg + 180) % 360
          if (orientation === 'flat') {
            // Flat-top hexagon target selection (opposite of source)
            if (oppositeAngle >= 330 || oppositeAngle < 30) connection.targetHandle = 'target_left'
            else if (oppositeAngle >= 30 && oppositeAngle < 60) connection.targetHandle = 'target_leftTop'
            else if (oppositeAngle >= 60 && oppositeAngle < 120) connection.targetHandle = 'target_top'
            else if (oppositeAngle >= 120 && oppositeAngle < 150) connection.targetHandle = 'target_rightTop'
            else if (oppositeAngle >= 150 && oppositeAngle < 210) connection.targetHandle = 'target_right'
            else if (oppositeAngle >= 210 && oppositeAngle < 240) connection.targetHandle = 'target_rightBottom'
            else if (oppositeAngle >= 240 && oppositeAngle < 300) connection.targetHandle = 'target_bottom'
            else connection.targetHandle = 'target_leftBottom'
          } else {
            // Pointy-top hexagon (original)
            if (oppositeAngle >= 337.5 || oppositeAngle < 22.5) connection.targetHandle = 'target_left'
            else if (oppositeAngle >= 22.5 && oppositeAngle < 67.5) connection.targetHandle = 'target_top'
            else if (oppositeAngle >= 67.5 && oppositeAngle < 112.5) connection.targetHandle = 'target_top'
            else if (oppositeAngle >= 112.5 && oppositeAngle < 157.5) connection.targetHandle = 'target_top'
            else if (oppositeAngle >= 157.5 && oppositeAngle < 202.5) connection.targetHandle = 'target_right'
            else if (oppositeAngle >= 202.5 && oppositeAngle < 247.5) connection.targetHandle = 'target_bottom'
            else if (oppositeAngle >= 247.5 && oppositeAngle < 292.5) connection.targetHandle = 'target_bottom'
            else connection.targetHandle = 'target_bottom'
          }
        }
      }
    }

    return connection
  }
}
